/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Resolve,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import { IAccountData } from '@app/models/account-data.model';
import { AppNames } from '@app/models/AppNames';
import { ILoan } from '@app/models/loan.model';
import { LoanStatus } from '@app/models/Loan-Status';
import { ILosInterface } from '@app/models/los-interface.model';
import { Pages } from '@app/models/Pages';
import { IUserAccMarketPlace } from '@app/models/user-info.model';
import * as _ from 'lodash';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';

import { AccountService } from './account.service';
import { BookingService } from './booking.service';
import { FiProductsService } from './fi-products.service';
import { LoanInfoService } from './loan-info.service';
import { LoggingService } from './logging.service';
import { LosInterfaceService } from './los-interface.service';
import { ProposalService } from './proposal.service';
import { UnityDirectStateService } from './unity-direct-state.service';
import { UserInfoService } from './user-info.service';

@Injectable({
  providedIn: 'root'
})
export class ReviewResolver implements Resolve<any> {
  eLoanStatus = LoanStatus;

  constructor(
    private header: LoanInfoService,
    private bookingService: BookingService,
    private proposalService: ProposalService,
    private losInterfaceService: LosInterfaceService,
    private accountService: AccountService,
    private router: Router,
    private userInfoService: UserInfoService,
    private fiproducts: FiProductsService,
    private readonly ls: LoggingService,
    private readonly udStateService: UnityDirectStateService
  ) {}

  resolve(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): any {
    const isAppNameExpress: boolean =
      sessionStorage.getItem('appName') === AppNames.UNITY_EXPRESS;
    const id: string = this.router.getCurrentNavigation().extras.state?.id;
    const status: LoanStatus =
      this.router.getCurrentNavigation().extras.state?.status;
    return this.userInfoService.getUserInfo().pipe(
      concatMap((userInfoData: IUserAccMarketPlace) => {
        const getStatusCall = isAppNameExpress
          ? this.losInterfaceService
              .getLoanStatus(
                userInfoData[this.userInfoService.getAccountIndex(userInfoData)]
              )
              .pipe(catchError(() => of(<ILosInterface>{})))
          : of(<ILosInterface>{});
        const getCUNameCall = this.accountService.getAccount(
          userInfoData[this.userInfoService.getAccountIndex(userInfoData)]
        );
        const getUserInfo = of(userInfoData);
        return forkJoin([getStatusCall, getCUNameCall, getUserInfo]);
      }),
      concatMap(([statusInfo, accountDetails, ...remaining]) => {
        if (!isAppNameExpress && id && status) {
          // DIRECT
          if (status === LoanStatus.Proposal) {
            return combineLatest(
              of([statusInfo, accountDetails, ...remaining]),
              this.proposalService.getRatesForProposal(id),
              this.proposalService.getProposal({ id })
            ) as Observable<
              [[ILosInterface, IAccountData, IUserAccMarketPlace], any, ILoan]
            >;
          } else if (
            status === LoanStatus.Booking ||
            status === LoanStatus.Booked ||
            status === LoanStatus.Void
          ) {
            return combineLatest(
              of([statusInfo, accountDetails, ...remaining]),
              of({}),
              this.bookingService.getBooking({ id })
            ) as Observable<
              [[ILosInterface, IAccountData, IUserAccMarketPlace], {}, ILoan]
            >;
          }
        } else if (statusInfo.loanStatus === this.eLoanStatus.Proposal) {
          return combineLatest(
            of([statusInfo, accountDetails, ...remaining]),
            this.proposalService.getRatesForProposal(statusInfo.quoteLoanId),
            this.proposalService.getProposal({ id: statusInfo.quoteLoanId })
          ) as Observable<
            [[ILosInterface, IAccountData, IUserAccMarketPlace], any, ILoan]
          >;
        } else if (
          statusInfo.loanStatus === 'Booked' ||
          statusInfo.loanStatus === this.eLoanStatus.Booking ||
          statusInfo.loanStatus === this.eLoanStatus.Void
        ) {
          return combineLatest(
            of([statusInfo, accountDetails, ...remaining]),
            of({}),
            this.bookingService.getBooking({ id: statusInfo.quoteLoanId })
          ) as Observable<
            [[ILosInterface, IAccountData, IUserAccMarketPlace], {}, ILoan]
          >;
        } else if (_.isEmpty(status)) {
          if (accountDetails.isBundleEnabled) {
            this.router.navigate([Pages.Bundles]);
          } else {
            this.router.navigate([Pages.UnityExpress], {
              state: {
                id: statusInfo.quoteLoanId,
                status: statusInfo.loanStatus
              }
            });
          }
          return;
        }
      }),
      map(([[statusInfo, accountInfo, userInfo], proposalRates, loan]) => {
        // Populate userInfo in state service so it's available for downstream components
        const userAccountIndex = this.userInfoService.getAccountIndex(userInfo);
        this.udStateService.userInfo =
          userAccountIndex >= 0 ? userInfo[userAccountIndex] : userInfo[0];
        // To populate the data on header before page is loaded
        this.header.loanInfo.next({
          loName: `${userInfo[this.userInfoService.getAccountIndex(userInfo)].AccountName}`,
          loanStatus: statusInfo.loanStatus,
          creditUnionName: accountInfo.fiName
        });
        return { proposalRates, loan, statusInfo, userInfo, accountInfo };
      }),
      concatMap(
        ({ proposalRates, loan, statusInfo, userInfo, accountInfo }) => {
          return forkJoin({
            fiProducts: this.fiproducts.getFiProducts(
              userInfo[this.userInfoService.getAccountIndex(userInfo)]
            )
          }).pipe(
            map(({ fiProducts }) => ({
              fiProducts,
              proposalRates,
              loan,
              statusInfo,
              userInfo,
              accountInfo
            }))
          );
        }
      ),
      catchError(() => {
        //Redirecting to error page since we couldn't load the page without resolver data
        this.ls.sendErrorLevelMessage('Review Resolver Error', this, {
          error: 'There was an error completing the review resolver.'
        });
        return of(null);
      })
    );
  }
}
